import store from "../app/store";
import { toast } from "react-toastify";

/**
 * Requests a signed preview URL for a file from the backend using the file's key.
 *
 * - Sends a POST request to `/other/getSignedUrl`.
 * - Uses the bearer token from Redux store for authorization.
 * - Displays user-friendly toast notifications on error.
 *
 * @async
 * @function GetFilesPreview
 * @param {string} key - The unique key of the file stored (e.g., in S3 or similar).
 * @returns {Promise<{data: string | null, error: string | null}>} An object:
 * - `data`: Signed URL string on success, `null` on failure.
 * - `error`: `null` if no error, or error message string if failed.
 *
 * @example
 * const result = await GetFilesPreview("key -> aws s3 object key");
 * if (!result.error) {
 *   window.open(result.data);
 * } else {
 *   console.error("Preview error:", result.error);
 * }
 */

const GetFilesPreview = async (key) => {
  const serverDomain = process.env.REACT_APP_SERVER;
  const url = `${serverDomain}/other/getSignedUrl`;

  const token = store.getState().auth.token;

  if (!token) {
    toast.error("Unauthorized. Please log in again.");
    return { data: null, error: "No token found" };
  }

  const option = {
    method: "POST", // ✅ Fixed from PUT to POST
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ key }), // ✅ Raw key
  };

  try {
    const res = await fetch(url, option);
    if (!res.ok) {
      toast.error("Server Error, Try Again Later...");
      return { data: null, error: "Server error" };
    }

    const data = await res.json();

    if (!data?.data) {
      toast.error("Invalid file URL received.");
      return { data: null, error: "No signed URL returned" };
    }

    return { data: data?.data, error: null };
  } catch (err) {
    console.error(err);
    toast.error("Something went wrong.");
    return { data: null, error: "Fetch failed" };
  }
};

export default GetFilesPreview;
